export const INTEGRATION_CALLBACK_PATH = '/settings/integrations/callback';

export type IntegrationCallbackInput =
  | { kind: 'complete'; code: string; state: string }
  | { kind: 'provider-error' }
  | { kind: 'invalid' };

const INPUT_LIFETIME_MS = 10 * 60 * 1000;
const FILTERED = '[Filtered integration callback]';
const callbackValues = new Map<string, number>();
let pendingInput: IntegrationCallbackInput | null = null;
let pendingInputExpiresAt = 0;
let expiryTimer: ReturnType<typeof setTimeout> | undefined;

const pruneExpiredInput = (): void => {
  const now = Date.now();
  if (pendingInputExpiresAt <= now) pendingInput = null;
  for (const [value, expiresAt] of callbackValues) {
    if (expiresAt <= now) callbackValues.delete(value);
  }
};

const expireInput = (): void => {
  pruneExpiredInput();
  expiryTimer = undefined;
  if (callbackValues.size || pendingInput) scheduleExpiry();
};

const scheduleExpiry = (): void => {
  if (expiryTimer === undefined) {
    let nextExpiry = pendingInput ? pendingInputExpiresAt : Infinity;
    for (const expiresAt of callbackValues.values())
      nextExpiry = Math.min(nextExpiry, expiresAt);
    expiryTimer = setTimeout(expireInput, Math.max(0, nextExpiry - Date.now()));
  }
};

const callbackUrl = (value: string): URL | null => {
  try {
    const url = new URL(value, window.location.href);
    const path = decodeURIComponent(url.pathname)
      .replace(/\/+$/, '')
      .toLowerCase();
    return path === INTEGRATION_CALLBACK_PATH ? url : null;
  } catch {
    return null;
  }
};

const rememberValues = (url: URL): void => {
  const expiresAt = Date.now() + INPUT_LIFETIME_MS;
  for (const part of [url.search.slice(1), url.hash.slice(1)]) {
    if (part) callbackValues.set(part, expiresAt);
    for (const [key, value] of new URLSearchParams(part)) {
      if (value) {
        callbackValues.set(value, expiresAt);
        callbackValues.set(encodeURIComponent(value), expiresAt);
      }
      // Malformed returns can carry sensitive data in an unrecognized field.
      if (key && !part.includes('=')) callbackValues.set(key, expiresAt);
    }
    for (const field of part.split('&')) {
      const rawValue = field.slice(field.indexOf('=') + 1);
      if (rawValue) callbackValues.set(rawValue, expiresAt);
    }
  }
  if (callbackValues.size) scheduleExpiry();
};

const readInput = (url: URL): IntegrationCallbackInput => {
  try {
    decodeURIComponent(url.search.replace(/\+/g, ' '));
  } catch {
    return { kind: 'invalid' };
  }
  const params = url.searchParams;
  if (params.has('error')) return { kind: 'provider-error' };
  const code = params.getAll('code');
  const state = params.getAll('state');
  if (
    url.hash ||
    code.length !== 1 ||
    state.length !== 1 ||
    !code[0] ||
    !state[0]
  ) {
    return { kind: 'invalid' };
  }
  return { kind: 'complete', code: code[0], state: state[0] };
};

const captureInput = (url: URL): void => {
  rememberValues(url);
  pendingInput = readInput(url);
  pendingInputExpiresAt = Date.now() + INPUT_LIFETIME_MS;
  scheduleExpiry();
};

/** Consume in an effect into a ref, not a render/state initializer (Strict Mode). */
export const takeIntegrationCallbackInput =
  (): IntegrationCallbackInput | null => {
    const input = pendingInputExpiresAt > Date.now() ? pendingInput : null;
    pendingInput = null;
    return input;
  };

const urlPattern = /(?:https?:\/\/|\/)[^\s"'<>]*/gi;

const sanitizeText = (value: string): string => {
  const sanitized = value.replace(urlPattern, (candidate) => {
    const url = callbackUrl(candidate);
    if (!url) return candidate;
    rememberValues(url);
    return candidate.split(/[?#]/, 1)[0];
  });
  let result = sanitized;
  for (const secret of [...callbackValues.keys()].sort(
    (a, b) => b.length - a.length
  )) {
    // Short malformed values must not erase unrelated words or identifiers.
    const escaped = secret.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const pattern =
      secret.length < 4 ? `(?<![\\w])${escaped}(?![\\w])` : escaped;
    result = result.replace(new RegExp(pattern, 'g'), FILTERED);
  }
  return result;
};

/** Applies to SDK payloads, including nested error context and Replay events. */
export const sanitizeIntegrationCallbackTelemetry = <T>(payload: T): T => {
  pruneExpiredInput();
  const visited = new WeakSet<object>();
  const collect = (value: unknown): void => {
    if (typeof value === 'string') {
      for (const match of value.matchAll(urlPattern)) {
        const url = callbackUrl(match[0]);
        if (url) rememberValues(url);
      }
    } else if (value && typeof value === 'object' && !visited.has(value)) {
      visited.add(value);
      for (const [key, item] of Object.entries(value)) {
        collect(key);
        collect(item);
      }
    }
  };
  collect(payload);

  const copies = new WeakMap<object, unknown>();
  const sanitize = (value: unknown): unknown => {
    if (typeof value === 'string') return sanitizeText(value);
    if (!value || typeof value !== 'object') return value;
    if (copies.has(value)) return copies.get(value);
    const prototype = Object.getPrototypeOf(value);
    if (
      !Array.isArray(value) &&
      prototype !== Object.prototype &&
      prototype !== null
    ) {
      return value;
    }
    const copy: Record<string, unknown> | unknown[] = Array.isArray(value)
      ? []
      : {};
    copies.set(value, copy);
    for (const [key, item] of Object.entries(value)) {
      Object.defineProperty(copy, sanitizeText(key), {
        value: sanitize(item),
        enumerable: true,
        configurable: true,
        writable: true,
      });
    }
    return copy;
  };
  return sanitize(payload) as T;
};

if (typeof window !== 'undefined') {
  const { history } = window;
  const originalReplace = history.replaceState;

  const cleanLocation = (): void => {
    const url = callbackUrl(window.location.href);
    if (!url || (!url.search && !url.hash)) return;
    captureInput(url);
    // Runs before SDK startup: rrweb's initial Meta event has no masking hook.
    originalReplace.call(history, history.state, '', url.pathname);
  };
  cleanLocation();

  for (const method of ['pushState', 'replaceState'] as const) {
    const original = history[method];
    history[method] = function (
      data: unknown,
      unused: string,
      destination?: string | URL | null
    ): void {
      const url = destination == null ? null : callbackUrl(String(destination));
      if (
        url &&
        url.origin === window.location.origin &&
        (url.search || url.hash)
      ) {
        original.call(this, data, unused, url.pathname);
        captureInput(url);
      } else {
        original.call(this, data, unused, destination);
      }
    };
  }
  // Before SDK listeners read a history entry created before this bundle loaded.
  window.addEventListener('popstate', cleanLocation, true);
}
